<script setup lang="ts">
	import { serviceDescriptor } from '@/config/services'
	import { computed } from 'vue'

	import ServiceIcon from '@/components/ServiceIcon.vue'

	import { Policy } from '@/interfaces/policy'

	import PolicyEntry from './PolicyEntry.vue'

	interface Props {
		policies: Policy[]
	}

	const props = defineProps<Props>()

	const groupedPolicies = computed(() => {
		const groups: Record<string, Policy[]> = {}
		props.policies.forEach((policy) => {
			const service = policy.serviceName ?? 'Otros'
			if (!groups[service]) groups[service] = []
			groups[service].push(policy)
		})
		return groups
	})

	// Las claves de este mapa estaban en español (`Vuelo`, `Auto`) mientras el dato
	// llega como `services.name` en inglés, así que Plane y Car nunca hacían match y
	// caían al gris del fallback. Del registry, que además resuelve en cualquier
	// casing.
	// `badge` no trae `border-*`, y el contenedor tiene `border`: con solo el badge el
	// header perdía el borde tintado que aportaba el mapa viejo. Se le suma el acento como
	// color de borde.
	const getServiceColor = (service: string) => {
		const descriptor = serviceDescriptor(service)

		return [descriptor.badge, descriptor.accent.replace('text-', 'border-')]
	}

	/** "Vuelo" en vez del `Plane` crudo que llega en la llave de agrupación. */
	const getServiceLabel = (service: string) => serviceDescriptor(service).label
</script>

<template>
	<div class="flex flex-col gap-6">
		<div
			v-for="service in Object.keys(groupedPolicies)"
			:key="service"
		>
			<!-- Service header -->
			<div
				class="flex items-center justify-between px-4 py-3 rounded-xl border mb-3"
				:class="getServiceColor(service)"
			>
				<div class="flex items-center gap-2">
					<ServiceIcon :service="service" />
					<span class="font-semibold text-sm">{{
						getServiceLabel(service)
					}}</span>
				</div>
				<span class="text-xs font-semibold opacity-70">
					{{ groupedPolicies[service].length }}
					{{ groupedPolicies[service].length === 1 ? 'política' : 'políticas' }}
				</span>
			</div>

			<!-- Policy cards -->
			<div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
				<PolicyEntry
					v-for="policy in groupedPolicies[service]"
					:key="policy.id"
					:policy="policy"
				/>
			</div>
		</div>
	</div>
</template>
